import { useState } from "react";
import { ChevronLeft, ChevronRight, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, todayAthens } from "../../../lib/format";
import { addDays, errMsg, hours, mondayOf } from "../../../lib/xutil";
import { Field, btnPrimary, input } from "../../../components/common/Page";

export const WeekNav = ({ week, setWeek }) => {
  const { t } = useI18n();
  const b = "grid h-10 w-10 place-items-center rounded-full bg-white shadow-sm";
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={b} onClick={() => setWeek(addDays(week, -7))} aria-label={t("x.prev")} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></button>
      <span className="text-sm font-medium text-navy" data-testid="week-label">{t("x.hr.week", { d: fmtDate(week) })}</span>
      <button type="button" className={b} onClick={() => setWeek(addDays(week, 7))} aria-label={t("x.next")} data-testid="week-next"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
};

export const useWeek = () => useState(mondayOf(todayAthens()));

const CorrectDialog = ({ row, onClose, onSaved }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ clock_in: row.in || "", clock_out: row.out || "", breaks: row.breaks.map((b) => ({ start: b.start, end: b.end || "" })), reason: "" });
  const setB = (i, k, v) => setF((s) => ({ ...s, breaks: s.breaks.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const save = async () => {
    try {
      await api.put(`/hr/entries/${row.user_id}/${row.date}`, { clock_in: f.clock_in || null, clock_out: f.clock_out || null, breaks: f.breaks.filter((b) => b.start && b.end), reason: f.reason });
      toast.success(t("x.saved")); onSaved();
    } catch (e) { toast.error(errMsg(t, e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="correct-dialog">
        <DialogHeader><DialogTitle>{t("x.hr.correctTitle", { name: row.name, date: fmtDate(row.date) })}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("x.hr.in")}><input type="text" inputMode="numeric" maxLength={5} placeholder="08:00" pattern="[0-2][0-9]:[0-5][0-9]" value={f.clock_in} onChange={(e) => setF((s) => ({ ...s, clock_in: e.target.value }))} className={input} data-testid="correct-in" /></Field>
            <Field label={t("x.hr.out")}><input type="text" inputMode="numeric" maxLength={5} placeholder="08:00" pattern="[0-2][0-9]:[0-5][0-9]" value={f.clock_out} onChange={(e) => setF((s) => ({ ...s, clock_out: e.target.value }))} className={input} data-testid="correct-out" /></Field>
          </div>
          {f.breaks.map((b, i) => (
            <div key={i} className="flex items-end gap-2">
              <Field label={t("x.hr.break")}><input type="text" inputMode="numeric" maxLength={5} placeholder="08:00" pattern="[0-2][0-9]:[0-5][0-9]" value={b.start} onChange={(e) => setB(i, "start", e.target.value)} className={input} /></Field>
              <Field label="–"><input type="text" inputMode="numeric" maxLength={5} placeholder="08:00" pattern="[0-2][0-9]:[0-5][0-9]" value={b.end} onChange={(e) => setB(i, "end", e.target.value)} className={input} /></Field>
              <button type="button" onClick={() => setF((s) => ({ ...s, breaks: s.breaks.filter((_, j) => j !== i) }))} className="grid h-11 w-11 shrink-0 place-items-center rounded-full hover:bg-slate-100" aria-label="remove"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setF((s) => ({ ...s, breaks: [...s.breaks, { start: "", end: "" }] }))} className="inline-flex h-10 items-center gap-1 text-sm font-medium text-navy" data-testid="correct-add-break"><Plus className="h-4 w-4" />{t("x.hr.addBreak")}</button>
          <Field label={t("x.reason")}><textarea rows={2} value={f.reason} onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))} placeholder={t("x.hr.reasonPh")} className={`${input} h-auto py-2`} data-testid="correct-reason" /></Field>
          <button type="button" disabled={f.reason.trim().length < 3} onClick={save} className={btnPrimary} data-testid="correct-save">{t("x.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DaysTable = ({ days, editable, onChange, testId }) => {
  const { t } = useI18n();
  const [edit, setEdit] = useState(null);
  const td = "px-3 py-2.5 text-sm tabular-nums";
  const today = todayAthens();
  return (
    <div className="-mx-4 overflow-x-auto sm:mx-0" data-testid={testId}>
      <table className="w-full min-w-[620px]">
        <thead><tr className="text-left text-xs text-slate-500">{["", t("x.hr.scheduled"), t("x.hr.in"), t("x.hr.out"), t("x.hr.break"), t("x.hr.worked"), ""].map((h, i) => <th key={i} className={td}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {days.map((d, i) => (
            <tr key={d.date} className="text-navy" data-testid={`day-${d.user_id}-${d.date}`}>
              <td className={`${td} font-medium`}>{t("x.hr.days")[i % 7]} {fmtDate(d.date).slice(0, 5)}</td>
              <td className={td}>{d.holiday ? <span className="text-brand">{t("x.hr.holiday")}</span> : d.leave ? <span className="text-amber-600">{t(`x.hr.leave.types.${d.leave}`)}</span> : d.scheduled_start ? `${d.scheduled_start}–${d.scheduled_end}` : t("x.hr.settings.off")}</td>
              <td className={td}>{d.in || "—"}</td><td className={td}>{d.out || "—"}</td><td className={td}>{d.break_min ? `${d.break_min}′` : ""}</td>
              <td className={`${td} font-semibold`}>{d.worked_min ? hours(d.worked_min) : ""}{d.corrected && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">{t("x.hr.corrected")}</span>}</td>
              <td className={td}>{editable && d.date <= today && <button type="button" onClick={() => setEdit(d)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100" aria-label={t("x.hr.correct")} data-testid={`correct-${d.user_id}-${d.date}`}><Pencil className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit && <CorrectDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChange(); }} />}
    </div>
  );
};
